import dayjs from 'dayjs';
import React, { FC, useRef, useState } from 'react';
import DatePicker, { ReactDatePickerProps } from 'react-datepicker';
import AnimationToggleRotate from '../../animations/animation-toggle-rotate/animation-toggle-rotate';
import IcomoonIcon from '../icomoon-icon/icomoon-icon';

interface Props extends ReactDatePickerProps {
  className?: string;
  disabled?: boolean;
  error?: boolean;
  placeholder?: string;
}

const FieldDatepicker: FC<Props> = (props: Props) => {
  const { className, disabled, error, onChange, placeholder, value } = props;
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const datepickerRef = useRef(null);

  let background = 'bg-light-blue';
  let ring = 'ring-inset focus:ring-1';
  let rounded = 'rounded-md';
  let iconColor = 'french-blue';
  let selectedValueTextColor = 'text-black';

  if (disabled) {
    background = 'bg-grey-50';
    iconColor = 'grey-600';
  }

  if (isFocused) {
    ring = 'ring-inset ring-focus ring-1';
    rounded = 'rounded-t-md';
  }

  if (error) {
    background = 'bg-invalid-bg';
    ring = 'ring-inset ring-invalid ring-1';
    iconColor = 'invalid';
  }

  if (!value) {
    selectedValueTextColor = 'text-grey-600';
  }

  return (
    <div className={`relative ${className}`}>
      <button
        type="button"
        className={`
          w-full text-left flex items-center px-6 py-4 focus:outline-none
          ${ring}
          ${background}
          ${rounded}
        `}
        onClick={() => {
          (datepickerRef.current as DatePicker).setOpen(!isFocused);
          setIsFocused(!isFocused);
        }}
      >
        <span
          className={`inline-block flex-grow truncate ${selectedValueTextColor}`}
        >
          {value ? dayjs(value).format('MMMM YYYY') : placeholder}
        </span>
        <span className="flex-shrink-0 ml-4">
          <AnimationToggleRotate isRotated={isFocused}>
            <IcomoonIcon name="chevron-down" color={iconColor} size="base" />
          </AnimationToggleRotate>
        </span>
      </button>

      <DatePicker
        {...props}
        ref={datepickerRef}
        className="absolute top-0"
        onChange={onChange}
        onClickOutside={() => setIsFocused(false)}
        placeholderText={placeholder}
        selected={new Date(value)}
      />
    </div>
  );
};

FieldDatepicker.defaultProps = {
  className: '',
  disabled: false,
  error: false,
  placeholder: null,
};

export default FieldDatepicker;
